/** Directorio de torneos públicos (Eduardo 2026-09-26): textos compartidos
 * por la landing (torneos123.com) y la sección «Torneos públicos» del
 * jugador. Funciones puras para poder probarlas. */

export interface DirectoryCategoryLike {
  name: string;
  gender?: string | null;
  play_mode?: string | null;
  birth_year_from?: number | null;
  birth_year_to?: number | null;
  is_full?: boolean;
  /** Cerrada por fecha/inicio/cupo; registration_deadline = cierre efectivo. */
  is_closed?: boolean;
  registration_deadline?: string | null;
}

/** Cierres de inscripción para la tarjeta del torneo (cierre por categoría,
 * Eduardo 2026-10-24). Si todas las categorías comparten fecha y están
 * abiertas: una sola fecha («Inscripciones hasta el …», como antes). Si no,
 * una línea por modalidad: «Dobles · inscripciones hasta el 30 nov» o
 * «Singles: cerradas». */
export type DeadlineSummary =
  | { kind: "single"; deadline: string }
  | { kind: "mixed"; lines: string[] }
  | null;

export function categoriesDeadlineSummary(
  categories: DirectoryCategoryLike[],
  generalDeadline: string | null | undefined,
): DeadlineSummary {
  if (categories.length === 0) return generalDeadline ? { kind: "single", deadline: generalDeadline } : null;
  const deadlines = new Set(categories.map((c) => c.registration_deadline ?? generalDeadline ?? ""));
  const anyClosed = categories.some((c) => c.is_closed);
  if (deadlines.size === 1 && !anyClosed) {
    const [only] = [...deadlines];
    return only ? { kind: "single", deadline: only } : null;
  }
  const lines: string[] = [];
  for (const [mode, label] of [["singles", "Singles"], ["doubles", "Dobles"]] as const) {
    const ofMode = categories.filter((c) => (c.play_mode === "doubles" ? "doubles" : "singles") === mode);
    if (ofMode.length === 0) continue;
    const open = ofMode.filter((c) => !c.is_closed);
    if (open.length === 0) {
      lines.push(`${label}: cerradas`);
      continue;
    }
    const dates = open.map((c) => c.registration_deadline ?? generalDeadline ?? "").filter(Boolean).sort();
    const last = dates[dates.length - 1];
    lines.push(last ? `${label} · inscripciones hasta el ${shortDayMonth(last)}` : `${label} · inscripciones abiertas`);
  }
  // Abiertas primero (como en la maqueta).
  lines.sort((a, b) => Number(a.endsWith(": cerradas")) - Number(b.endsWith(": cerradas")));
  return { kind: "mixed", lines };
}

function shortDayMonth(iso: string): string {
  const d = parseIsoDate(iso);
  return d ? `${d.day} ${MONTHS[d.month - 1]}` : iso;
}

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function parseIsoDate(value: string | null | undefined): { day: number; month: number; year: number } | null {
  // "YYYY-MM-DD" sin zona: se lee tal cual (new Date() lo movería un día).
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** "12 – 14 oct 2026", "30 sep – 2 oct 2026", "5 oct 2026". */
export function formatDirectoryDates(start: string | null | undefined, end: string | null | undefined): string {
  const from = parseIsoDate(start);
  const to = parseIsoDate(end);
  if (!from) return "Fecha por anunciar";
  const label = (d: { day: number; month: number; year: number }, withYear: boolean) =>
    `${d.day} ${MONTHS[d.month - 1]}${withYear ? ` ${d.year}` : ""}`;
  if (!to || (to.year === from.year && to.month === from.month && to.day === from.day)) {
    return label(from, true);
  }
  if (to.year === from.year && to.month === from.month) {
    return `${from.day} – ${to.day} ${MONTHS[to.month - 1]} ${to.year}`;
  }
  if (to.year === from.year) {
    return `${label(from, false)} – ${label(to, true)}`;
  }
  return `${label(from, true)} – ${label(to, true)}`;
}

/** "Desde $650" / "Desde $1,200" (sin centavos si son cero). */
export function formatPriceFrom(price: string | null | undefined): string | null {
  if (!price) return null;
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  const text = amount.toLocaleString("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return `Desde $${text}`;
}

/** Rango de edad de una categoría en años de nacimiento. */
export function categoryBirthYearsLabel(category: DirectoryCategoryLike): string | null {
  const from = category.birth_year_from ?? null;
  const to = category.birth_year_to ?? null;
  if (from !== null && to !== null) return from === to ? `Nacidos en ${from}` : `Nacidos ${from}–${to}`;
  if (from !== null) return `Nacidos en ${from} o después`;
  if (to !== null) return `Nacidos en ${to} o antes`;
  return null;
}

export function categoryGenderLabel(gender: string | null | undefined): string | null {
  if (gender === "male") return "Varonil";
  if (gender === "female") return "Femenil";
  if (gender === "mixed") return "Mixto";
  return null;
}

/** "3 categorías · Singles y dobles" / "Sin categorías". */
export function categoriesSummary(categories: DirectoryCategoryLike[]): string {
  if (categories.length === 0) return "Categoría única";
  const modes = new Set(categories.map((c) => (c.play_mode === "doubles" ? "doubles" : "singles")));
  const modeText = modes.size === 2 ? "Singles y dobles" : modes.has("doubles") ? "Dobles" : "Singles";
  const count = categories.length === 1 ? "1 categoría" : `${categories.length} categorías`;
  return `${count} · ${modeText}`;
}

/** Filtro de texto de la página de torneos (sin acentos ni mayúsculas). */
export function matchesDirectoryQuery(
  tournament: { name: string; venue_name?: string | null; organizer_name?: string | null },
  query: string,
): boolean {
  const normalize = (value: string | null | undefined) =>
    (value ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim();
  const needle = normalize(query);
  if (!needle) return true;
  return [tournament.name, tournament.venue_name, tournament.organizer_name].some((field) =>
    normalize(field).includes(needle),
  );
}
